import {
    Card,
    CardContent,
    Typography,
    Stack,
    Divider,
    Box,
    IconButton,
    Link,
    Chip,
    Skeleton,
    Alert,
    Button
} from '@mui/material';


import moment from 'moment-timezone';

import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import { useDispatch, useSelector } from 'react-redux';
import { LockOutlined } from '@mui/icons-material';
import { setShowLoginForm } from '../../redux/actions/authActions';

const BookingSummary = ({ bookingDetails }) => {
    const summary = bookingDetails?.summary;
    const userData = useSelector(state => state.auth.userData);
    const dispatch = useDispatch();

    const formatDate = (unixTs, timezone = 'Asia/Kolkata') => {
        if (!unixTs) return '';

        return moment
            .unix(unixTs)            // unix timestamp in seconds
            .tz(timezone)            // ensure correct timezone
            .format('DD MMM, YYYY'); // e.g. 22 Dec, 2025
    };

    if (!summary) return null;

    const isAuthorisedUser = userData?.id &&
        (
            userData?.id === bookingDetails?.customerId ||
            userData?.email === bookingDetails?.customerEmail ||
            userData?.telephone === bookingDetails?.customerPhoneNumber
        );


    return (
        <Card
            variant="outlined"
            sx={{
                my: 3,
                borderRadius: 5,
            }}
        >
            <CardContent>
                {isAuthorisedUser ?
                    <Stack spacing={2}>
                        {/* Header */}
                        <Typography variant="h6" fontWeight={600}>
                            Booking Summary
                        </Typography>

                        <Divider />

                        {/* Note */}
                        {summary?.note && (
                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                <DescriptionIcon color="primary" />
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        Notes

                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {summary.note}
                                    </Typography>
                                </Box>
                            </Stack>
                        )}

                        {/* Attachments */}
                        {summary?.attachments?.length > 0 && (
                            <>
                                <Divider />
                                <Stack spacing={1}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <AttachFileIcon color="primary" />
                                        <Typography variant="subtitle2" fontWeight={600}>
                                            Attachments
                                        </Typography>
                                    </Stack>

                                    {summary.attachments.map((file, index) => (
                                        <Stack
                                            key={index}
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            sx={{
                                                p: 1,
                                                borderRadius: 1,
                                                border: '1px solid #e0e0e0'
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{ maxWidth: '70%' }}
                                                noWrap
                                            >
                                                {file.originName ?? file.keyName}
                                            </Typography>

                                            <IconButton
                                                component={Link}
                                                href={file.fileUrl}
                                                target="_blank"
                                                download
                                            >
                                                <DownloadIcon />
                                            </IconButton>
                                        </Stack>
                                    ))}
                                </Stack>
                            </>
                        )}

                        {/* Follow up */}
                        {summary?.followUp?.remindOn && (
                            <>
                                <Divider />
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <EventRepeatIcon color="primary" />
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        Next Follow-up
                                    </Typography>

                                    <Chip
                                        label={formatDate(summary.followUp.remindOn, bookingDetails.timezoneId)}
                                        color="warning"
                                        size="small"
                                    />
                                </Stack>
                            </>
                        )}
                    </Stack>
                    :
                    <>
                        <>
                            {/* Skeleton Version of Booking Summary */}
                            <Alert
                                severity="warning"
                                icon={<LockOutlined />}
                                action={
                                    <Button
                                        color="inherit"
                                        size="small"
                                        onClick={() => dispatch(setShowLoginForm(true))}
                                    >
                                        Login
                                    </Button>
                                }
                            >
                                {userData.id
                                    ? "Please login using the same account that created this booking."
                                    : "You need to login to view the booking summary."}
                            </Alert>
                            <Stack spacing={2} sx={{ mt: 2 }}>
                                <Skeleton variant="text" width={200} height={30} animation={false}/>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Box sx={{ minWidth: 60 }}>
                                        <Skeleton variant="rounded" height={60} animation={false} />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Skeleton variant="text" width={150} height={30} animation={false} />
                                        <Skeleton variant="text" width={"100%"} height={30} animation={false}/>
                                    </Box>
                                </Box>
                                <Divider />
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Box sx={{ minWidth: 30 }}>
                                        <Skeleton variant="rounded" height={30} animation={false}/>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Skeleton variant="text" height={30} animation={false}/>
                                    </Box>
                                </Box>
                                <Skeleton variant="rectangular" height={40} animation={false}/>
                                <Divider />
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Box sx={{ minWidth: 30 }}>
                                        <Skeleton variant="rounded" height={30} animation={false} />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Skeleton variant="text" height={30} animation={false}/>
                                    </Box>
                                </Box>
                            </Stack>
                        </>
                    </>
                }
            </CardContent>
        </Card>
    );
};

export default BookingSummary;